/**
 * Accessibility utilities for keyboard navigation and ARIA attributes
 */

/**
 * Handle keyboard events for interactive elements
 */
export const handleKeyboardNavigation = (
  event: React.KeyboardEvent,
  onAction: () => void,
  options?: {
    keys?: string[];
    preventDefault?: boolean;
  }
) => {
  const { keys = ['Enter', ' '], preventDefault = true } = options || {};
  
  if (keys.includes(event.key)) {
    if (preventDefault) {
      event.preventDefault();
    }
    onAction();
  }
};

/**
 * Get ARIA label for language selector
 */
export const getAriaLabel = (language: string, action: string): string => {
  const labels: Record<string, Record<string, string>> = {
    pt: {
      changeLanguage: 'Alterar idioma',
      openMenu: 'Abrir menu',
      closeMenu: 'Fechar menu',
      navigateTo: 'Navegar para',
      submitForm: 'Submeter formulário',
      calculate: 'Calcular',
      reset: 'Limpar',
      next: 'Próximo',
      previous: 'Anterior',
      close: 'Fechar'
    },
    en: {
      changeLanguage: 'Change language',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      navigateTo: 'Navigate to',
      submitForm: 'Submit form',
      calculate: 'Calculate',
      reset: 'Reset',
      next: 'Next',
      previous: 'Previous',
      close: 'Close'
    }
  };

  return labels[language]?.[action] || action;
};

/**
 * Get ARIA live region announcement
 */
export const announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;
  
  document.body.appendChild(announcement);
  
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

/**
 * Focus management utilities
 */
export const focusManagement = {
  /**
   * Trap focus within an element
   */
  trapFocus: (element: HTMLElement) => {
    const focusableElements = element.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    element.addEventListener('keydown', handleTabKey);
    
    return () => {
      element.removeEventListener('keydown', handleTabKey);
    };
  },

  /**
   * Focus first focusable element
   */
  focusFirst: (element: HTMLElement) => {
    const focusable = element.querySelector<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    focusable?.focus();
  },

  /**
   * Save and restore focus
   */
  saveFocus: () => {
    const activeElement = document.activeElement as HTMLElement;
    return activeElement;
  },

  restoreFocus: (element: HTMLElement | null) => {
    element?.focus();
  }
};

/**
 * Skip to main content link component
 * Note: This is a utility function, not a React component
 * Use the inline version in App.tsx instead
 */

